(function () {
  'use strict';

  const PRESERVE_SELECTOR = '.image-swatch-grid[data-preserve]';
  const seedCache = new Map();
  const scheduledProducts = new WeakMap();

  const nativeScrollIntoView = Element.prototype.scrollIntoView;
  const nativeFocus = HTMLElement.prototype.focus;

  let pendingVariantUpdate = null;
  let pendingClearTimer = null;

  HTMLElement.prototype.focus = function (options) {
    if (
      pendingVariantUpdate &&
      pendingVariantUpdate.productInfo &&
      pendingVariantUpdate.productInfo.contains(this)
    ) {
      if (options && typeof options === 'object') {
        return nativeFocus.call(this, { ...options, preventScroll: true });
      }
      return nativeFocus.call(this, { preventScroll: true });
    }

    return nativeFocus.call(this, options);
  };

  Element.prototype.scrollIntoView = function (...args) {
    if (
      pendingVariantUpdate &&
      pendingVariantUpdate.productInfo &&
      pendingVariantUpdate.productInfo.contains(this)
    ) {
      return;
    }

    return nativeScrollIntoView.apply(this, args);
  };

  function escapeSelector(value) {
    const stringValue = String(value ?? '');
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(stringValue);
    }
    return stringValue.replace(/([ #;&,.+*~':"!^$[\]()=>|/@\\])/g, '\\$1');
  }

  function getGridPairs(grid) {
    return Array.from(grid.querySelectorAll('input[type="radio"][id]'))
      .map((input) => {
        const label = grid.querySelector(`label[for="${escapeSelector(input.id)}"]`);
        return label ? { input, label } : null;
      })
      .filter(Boolean);
  }

  function getPairByOptionValueId(grid, optionValueId) {
    if (!optionValueId) return null;

    const input = grid.querySelector(
      `input[type="radio"][data-option-value-id="${escapeSelector(optionValueId)}"]`
    );
    if (!input) return null;

    const label = grid.querySelector(`label[for="${escapeSelector(input.id)}"]`);
    if (!label) return null;

    return { input, label };
  }

  function labelHasResolvedImage(label) {
    return !!label?.querySelector('.image-swatch__image[src]');
  }

  function copyResolvedMedia(sourceLabel, destinationLabel, onlyIfMissing = false) {
    if (!sourceLabel || !destinationLabel) return;
    if (onlyIfMissing && labelHasResolvedImage(destinationLabel)) return;

    const sourceMedia = sourceLabel.querySelector('.image-swatch__media');
    const destinationMedia = destinationLabel.querySelector('.image-swatch__media');
    if (!sourceMedia || !destinationMedia) return;

    const sourceImage = sourceMedia.querySelector('.image-swatch__image');
    const sourceColor = sourceMedia.querySelector('.image-swatch__color');
    const sourcePlaceholder = sourceMedia.querySelector('.image-swatch__placeholder');
    const currentCross = destinationMedia.querySelector('.image-swatch__cross');

    destinationMedia.innerHTML = '';

    if (sourceImage) {
      destinationMedia.appendChild(sourceImage.cloneNode(true));
    } else if (sourceColor) {
      destinationMedia.appendChild(sourceColor.cloneNode(true));
    } else if (sourcePlaceholder) {
      destinationMedia.appendChild(sourcePlaceholder.cloneNode(true));
    }

    if (currentCross) {
      destinationMedia.appendChild(currentCross.cloneNode(true));
    }
  }

  function getCurrentRequestUrl(productInfo, variantSelects) {
    const checkedInput = variantSelects?.querySelector(
      'input[type="radio"][data-product-url]:checked'
    );

    const checkedProductUrl = checkedInput?.getAttribute('data-product-url');
    if (checkedProductUrl) return checkedProductUrl;

    const selectedInput = variantSelects?.querySelector(
      'input[type="radio"][data-product-url][checked]'
    );

    const selectedProductUrl = selectedInput?.getAttribute('data-product-url');
    if (selectedProductUrl) return selectedProductUrl;

    return (
      productInfo?.dataset?.url ||
      variantSelects?.dataset?.url ||
      window.location.pathname
    );
  }

  function getSectionId(productInfo, variantSelects) {
    return (
      productInfo?.sectionId ||
      productInfo?.dataset?.section ||
      variantSelects?.dataset?.section
    );
  }

  async function getSeedDocument(productInfo, variantSelects) {
    const requestUrl = getCurrentRequestUrl(productInfo, variantSelects);
    const baseUrl = new URL(requestUrl, window.location.origin);
    baseUrl.search = '';
    baseUrl.hash = '';

    const sectionId = getSectionId(productInfo, variantSelects);
    if (sectionId) {
      baseUrl.searchParams.set('section_id', sectionId);
    }

    const cacheKey = baseUrl.toString();

    if (!seedCache.has(cacheKey)) {
      seedCache.set(
        cacheKey,
        fetch(cacheKey, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((error) => {
            seedCache.delete(cacheKey);
            throw error;
          })
      );
    }

    return seedCache.get(cacheKey);
  }

  async function hydrateMissingImages(productInfo) {
    const variantSelects = productInfo?.querySelector('variant-selects');
    if (!variantSelects) return;

    const currentGrids = Array.from(variantSelects.querySelectorAll(PRESERVE_SELECTOR));
    if (!currentGrids.length) return;

    const hasMissing = currentGrids.some((grid) =>
      getGridPairs(grid).some(({ label }) => !labelHasResolvedImage(label))
    );

    if (!hasMissing) return;

    let seedDoc;
    try {
      seedDoc = await getSeedDocument(productInfo, variantSelects);
    } catch (error) {
      console.error('[variant-image-swatches] seed fetch failed:', error);
      return;
    }

    const seedVariantSelects = seedDoc.querySelector('variant-selects');
    if (!seedVariantSelects) return;

    currentGrids.forEach((currentGrid) => {
      const preserveKey = currentGrid.getAttribute('data-preserve');
      const seedGrid =
        seedVariantSelects.querySelector(
          `[data-preserve="${escapeSelector(preserveKey)}"]`
        ) || seedVariantSelects.querySelector(PRESERVE_SELECTOR);

      if (!seedGrid) return;

      getGridPairs(currentGrid).forEach(({ input, label }) => {
        if (labelHasResolvedImage(label)) return;

        const optionValueId = input.getAttribute('data-option-value-id');
        const seedPair = getPairByOptionValueId(seedGrid, optionValueId);
        if (!seedPair) return;

        copyResolvedMedia(seedPair.label, label, true);
      });
    });
  }

  function beginPendingVariantUpdate(productInfo) {
    if (!productInfo) return;

    pendingVariantUpdate = {
      productInfo,
      startedAt: Date.now()
    };

    if (pendingClearTimer) {
      clearTimeout(pendingClearTimer);
    }

    pendingClearTimer = setTimeout(() => {
      pendingVariantUpdate = null;
      pendingClearTimer = null;
    }, 1200);
  }

  function clearPendingVariantUpdate(productInfo) {
    if (!pendingVariantUpdate) return;
    if (productInfo && pendingVariantUpdate.productInfo !== productInfo) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (pendingVariantUpdate && (!productInfo || pendingVariantUpdate.productInfo === productInfo)) {
          pendingVariantUpdate = null;
        }
        if (pendingClearTimer) {
          clearTimeout(pendingClearTimer);
          pendingClearTimer = null;
        }
      });
    });
  }

  function scheduleHydration(productInfo) {
    if (!productInfo) return;

    const existingTask = scheduledProducts.get(productInfo);
    if (existingTask) {
      cancelAnimationFrame(existingTask);
    }

    const taskId = requestAnimationFrame(() => {
      scheduledProducts.delete(productInfo);
      hydrateMissingImages(productInfo);
      clearPendingVariantUpdate(productInfo);
    });

    scheduledProducts.set(productInfo, taskId);
  }

  function bindVariantUpdateCapture(productInfo) {
    if (!productInfo || productInfo.__variantUpdateCaptureBound) return;
    productInfo.__variantUpdateCaptureBound = true;

    productInfo.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target.closest('variant-selects')) {
          beginPendingVariantUpdate(productInfo);
        }
      },
      true
    );

    productInfo.addEventListener(
      'change',
      (event) => {
        if (event.target.closest('variant-selects')) {
          beginPendingVariantUpdate(productInfo);
        }
      },
      true
    );
  }

  function patchMediaGallery() {
    customElements.whenDefined('media-gallery').then(() => {
      const MediaGallery = customElements.get('media-gallery');
      if (!MediaGallery || MediaGallery.prototype.__variantImageSwatchPatched) return;

      MediaGallery.prototype.__variantImageSwatchPatched = true;
      const originalSetActiveMedia = MediaGallery.prototype.setActiveMedia;

      if (!originalSetActiveMedia) return;

      MediaGallery.prototype.setActiveMedia = function (...args) {
        const shouldBlock =
          pendingVariantUpdate &&
          pendingVariantUpdate.productInfo &&
          pendingVariantUpdate.productInfo.contains(this);

        if (shouldBlock) {
          const originalDatasetValue = this.dataset.autoScrollWindow;
          this.dataset.autoScrollWindow = 'false';

          const result = originalSetActiveMedia.apply(this, args);

          if (originalDatasetValue == null) {
            delete this.dataset.autoScrollWindow;
          } else {
            this.dataset.autoScrollWindow = originalDatasetValue;
          }

          return result;
        }

        return originalSetActiveMedia.apply(this, args);
      };
    });
  }

  function watchProductInfo(productInfo) {
    if (!productInfo || productInfo.__variantImageSwatchesObserved) return;
    productInfo.__variantImageSwatchesObserved = true;

    bindVariantUpdateCapture(productInfo);

    const observer = new MutationObserver((mutations) => {
      const shouldCheck = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          return (
            Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.matches?.('variant-selects, .image-swatch-grid, media-gallery') ||
                  node.querySelector?.('variant-selects, .image-swatch-grid, media-gallery'))
            ) ||
            Array.from(mutation.removedNodes).some((node) => node.nodeType === 1)
          );
        }

        if (mutation.type === 'attributes') {
          return (
            mutation.target.matches?.('input[type="radio"], img.image-swatch__image') ||
            mutation.attributeName === 'src' ||
            mutation.attributeName === 'checked'
          );
        }

        return false;
      });

      if (shouldCheck) {
        scheduleHydration(productInfo);
      }
    });

    observer.observe(productInfo, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'checked']
    });

    scheduleHydration(productInfo);
  }

  function boot() {
    document.querySelectorAll('product-info').forEach(watchProductInfo);
  }

  patchMediaGallery();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  const rootObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('product-info')) {
          watchProductInfo(node);
        } else {
          node.querySelectorAll?.('product-info').forEach(watchProductInfo);
        }
      });
    });
  });

  rootObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
